import { observer } from 'mobx-react-lite';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../../hooks/useStore';
import { Form, Formik, useFormikContext } from 'formik';
import CommonButton from '../../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../../components/form-components/common-components/text-field/FormikTextField';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import styles from './EditHospital.module.css';

type EditHospitalFormValues = {
  id: number;
  hospitalName: string;
  hospitalAddress: string;
  isHospitalClosed: boolean;
};

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<EditHospitalFormValues>();
  const { uiStore } = useStore();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton
        type="save"
        onClick={submitForm}
        disabled={isSubmitting}
        loading={uiStore.isLoading}
      >
        Güncelle
      </CommonButton>
    </>
  );
};

const validationSchema = Yup.object({
  hospitalName: Yup.string()
    .required('Hastane adı zorunludur')
    .min(2, 'Hastane adı en az 2 karakter olmalıdır'),
  hospitalAddress: Yup.string(),
});

const EditHospital = () => {
  const { popupStore, hospitalStore, uiStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const initialValues = {
    id: hospitalStore.selectedHospitalToEdit?.id || 0,
    hospitalName: hospitalStore.selectedHospitalToEdit?.hospitalName || '',
    hospitalAddress: hospitalStore.selectedHospitalToEdit?.hospitalLocation || '',
    isHospitalClosed: hospitalStore.selectedHospitalToEdit?.isHospitalClosed || false,
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (!values.id) {
        toast.error('Hastane ID bulunamadı');
        return;
      }

      uiStore.setLoading(true);
      const response = await hospitalStore.updateHospital(values.id, {
        id: values.id,
        hospitalName: values.hospitalName,
        hospitalLocation: values.hospitalAddress || undefined,
        isHospitalClosed: values.isHospitalClosed,
      });

      if (response.error === false) {
        toast.success('Hastane başarıyla güncellendi.');
        popupStore.setIsEditHospitalOpen(false);
        hospitalStore.setSelectedHospitalToEdit(null);
        hospitalStore.refreshGrid();
      } else {
        toast.error('Hastane güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Hastane güncellenirken hata:', error);
      toast.error('Hastane güncellenirken bir hata oluştu');
    } finally {
      uiStore.setLoading(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsEditHospitalOpen(false);
    hospitalStore.setSelectedHospitalToEdit(null);
    toast.dismiss();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values }) => (
        <Popup
          isOpen={popupStore.isEditHospitalOpen}
          onClose={handleClose}
          title="Hastane Düzenle"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="hospitalName"
                  label="Hastane Adı"
                  placeholder="Hastane adını giriniz"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="hospitalAddress"
                  label="Hastane Adresi"
                  placeholder="Hastane adresini giriniz"
                />
              </div>

              {isAdmin && (
                <div className={styles.inputContainer}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={values.isHospitalClosed}
                      onChange={e => setFieldValue('isHospitalClosed', e.target.checked)}
                      className={styles.checkbox}
                    />
                    Hastane Kapalı
                  </label>
                </div>
              )}
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditHospital);
